import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SeatUsageIndicator } from "@/components/team/SeatUsageIndicator";
import { UserPlus, X, Mail } from "lucide-react";
import { toast } from "sonner";

interface PendingInvite {
  email: string;
  role: 'admin' | 'member';
}

interface StepTeamInviteProps {
  workspaceId: string;
  maxSeats: number;
  onComplete: (invites: PendingInvite[]) => void;
  onSkip: () => void;
}

export const StepTeamInvite = ({ workspaceId, maxSeats, onComplete, onSkip }: StepTeamInviteProps) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddInvite = () => {
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail) {
      toast.error("Bitte gib eine E-Mail-Adresse ein");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      toast.error("Bitte gib eine gültige E-Mail-Adresse ein");
      return;
    }

    // Check if already in list
    if (pendingInvites.some(inv => inv.email === trimmedEmail)) {
      toast.error("Diese E-Mail-Adresse wurde bereits hinzugefügt");
      return;
    }

    // Check seat limit (owner takes 1 seat)
    if (pendingInvites.length + 1 >= maxSeats) {
      toast.error(`Maximale Anzahl von ${maxSeats} Sitzen erreicht`);
      return;
    }

    setPendingInvites([...pendingInvites, { email: trimmedEmail, role }]);
    setEmail("");
    setRole('member');
    toast.success("Einladung zur Liste hinzugefügt");
  };

  const handleRemoveInvite = (emailToRemove: string) => {
    setPendingInvites(pendingInvites.filter(inv => inv.email !== emailToRemove));
  };

  const handleComplete = () => {
    if (pendingInvites.length === 0) {
      toast.info("Keine Einladungen hinzugefügt. Du kannst später Teammitglieder einladen.");
      onSkip();
      return;
    }
    onComplete(pendingInvites);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddInvite();
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Team einladen</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Lade deine Teammitglieder ein, um gemeinsam bessere Entscheidungen zu treffen.
          Du kannst diesen Schritt auch überspringen und später Mitglieder hinzufügen.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-6">
        <SeatUsageIndicator 
          usedSeats={pendingInvites.length + 1} 
          maxSeats={maxSeats}
          activeMembers={1}
          pendingInvites={pendingInvites.length}
        />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Teammitglieder hinzufügen
            </CardTitle>
            <CardDescription>
              Gib die E-Mail-Adressen deiner Teammitglieder ein und weise ihnen eine Rolle zu.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,auto] gap-3">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@firma.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Rolle</Label>
                <Select value={role} onValueChange={(v) => setRole(v as 'admin' | 'member')}>
                  <SelectTrigger id="role" className="w-full md:w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Mitglied</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  type="button" 
                  onClick={handleAddInvite}
                  disabled={pendingInvites.length + 1 >= maxSeats}
                  className="w-full md:w-auto"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Hinzufügen
                </Button>
              </div>
            </div>

            {pendingInvites.length > 0 && (
              <div className="space-y-2 pt-4 border-t">
                <Label>Ausstehende Einladungen ({pendingInvites.length})</Label>
                <div className="space-y-2">
                  {pendingInvites.map((invite) => (
                    <div 
                      key={invite.email}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{invite.email}</p>
                          <p className="text-xs text-muted-foreground">
                            {invite.role === 'admin' ? 'Administrator' : 'Mitglied'}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveInvite(invite.email)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={onSkip}
            type="button"
          >
            Später einladen
          </Button>
          <Button
            onClick={handleComplete}
            type="button"
          >
            {pendingInvites.length > 0 
              ? `${pendingInvites.length} ${pendingInvites.length === 1 ? 'Einladung' : 'Einladungen'} versenden` 
              : 'Weiter'}
          </Button>
        </div>
      </div>
    </div>
  );
};
